import React from 'react';
import {
    showNiceDate,
    WidgetPanelDetailProps,
} from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';

import {
    initialVolumeCurveComponentState,
    VolumeCurveComponentState,
} from 'widgets/volumeCurve/store/component/VolumeCurveComponentState.ts';
import {
    initialVolumeCurveDataState,
    VolumeCurveDataState,
} from 'widgets/volumeCurve/store/data/VolumeCurveDataState.ts';
import {
    buildVolumeCurveRequest,
    volumeCurveRequestToStreamKey,
} from 'widgets/volumeCurve/store/data/request-utils.ts';
import { selectPortfolioSelection } from 'portfolio/store/selectors.ts';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper.ts';
import { buildVolumeCurveData } from 'widgets/volumeCurve/data/volumeCurveData.ts';
import {
    initialVolumeCurveConfiguration,
    VolumeCurveConfiguration,
} from 'widgets/volumeCurve/store/configuration/VolumeCurveConfiguration.ts';

export interface WidgetPanelVolumeCurveProps extends WidgetPanelDetailProps {
    mode: 'data' | 'timeSeries' | 'buckets' | 'breaks';
}

export default function WidgetPanelVolumeCurve(
    props: WidgetPanelVolumeCurveProps
) {
    const {
        widgetState,
        state,
        mode,
        widget: { id: widgetKey },
    } = props;

    const portfolioSelection = selectPortfolioSelection(state.state);
    const wrapper = new PortfolioSelectionWrapper(portfolioSelection);
    const componentState =
        (widgetState?.component as VolumeCurveComponentState) ??
        initialVolumeCurveComponentState;
    const config =
        (widgetState?.config as VolumeCurveConfiguration) ??
        initialVolumeCurveConfiguration;

    const request = wrapper.selectedOrder
        ? buildVolumeCurveRequest(
              wrapper.selectedSymbol!,
              wrapper.symbology,
              wrapper.selectedOrder,
              componentState
          )
        : undefined;

    const streamKey = request
        ? volumeCurveRequestToStreamKey(request)
        : undefined;

    const streamState: VolumeCurveDataState = streamKey
        ? state.state.streams.data['volumeCurve'][streamKey]?.data ??
          initialVolumeCurveDataState
        : initialVolumeCurveDataState;

    const data =
        streamState &&
        buildVolumeCurveData(streamState, config.barSizeInMinutes ?? 1);

    for (const b of data.bucketedData) {
        showNiceDate(b, 'startTime');
        showNiceDate(b, 'endTime');
        showNiceDate(b, 'midTime');
    }

    for (const b of data.breaksInTrading) {
        showNiceDate(b, 'from');
        showNiceDate(b, 'to');
    }

    switch (mode) {
        case 'timeSeries':
            return (
                <WidgetPanelGrid
                    key="timeSeries"
                    data={streamState?.timeSeries ?? []}
                    getRowDataId={(d) => d._i.toString()}
                    csvFileName={`${widgetKey}-timeSeries`}
                />
            );

        case 'buckets':
            return (
                <WidgetPanelGrid
                    key="buckets"
                    data={data?.bucketedData ?? []}
                    getRowDataId={(d) => d._i.toString()}
                    csvFileName={`${widgetKey}-bucketedData`}
                />
            );

        case 'breaks':
            return (
                <WidgetPanelGrid
                    key="breaks"
                    data={data?.breaksInTrading ?? []}
                    getRowDataId={(d) => d.from.toString() + d.to.toString()}
                    csvFileName={`${widgetKey}-breaksInTrading`}
                />
            );

        default:
            return (
                <div className="widget-panel">
                    <div className="title">Volume Curve Data</div>
                    <div className="field">
                        <span className={'label'}>Stream Key:</span>
                        {streamKey}
                    </div>
                    <JsonPanel data={data} />
                </div>
            );
    }
}

export function addVolumeCurvePanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const renderData = () => (
        <WidgetPanelVolumeCurve {...detailProps} mode={'data'} />
    );
    panes.push({
        menuItem: 'Data',
        render: renderData,
    });

    const renderBuckets = () => (
        <WidgetPanelVolumeCurve {...detailProps} mode={'buckets'} />
    );
    panes.push({
        menuItem: 'Buckets',
        render: renderBuckets,
    });

    const renderBreaks = () => (
        <WidgetPanelVolumeCurve {...detailProps} mode={'breaks'} />
    );
    panes.push({
        menuItem: 'Breaks',
        render: renderBreaks,
    });

    const renderTs = () => (
        <WidgetPanelVolumeCurve {...detailProps} mode={'timeSeries'} />
    );
    panes.push({
        menuItem: 'Stream',
        render: renderTs,
    });
}
